import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getDailyOpsBridge, GetDailyOpsBridgeOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Unplug, ShieldAlert, Clock, AlertTriangle, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type BridgeData = GetDailyOpsBridgeOutputType;

export default function DailyOpsBridge() {
  const { user } = useAuth();
  const [data, setData] = useState<BridgeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getDailyOpsBridge({}).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-48" /><Skeleton className="h-32" /></div>;

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex items-center gap-3">
        <Unplug className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-xl font-bold">Daily Ops Bridge</h1>
          <p className="text-sm text-muted-foreground">Read-only operational data from Daily Ops</p>
        </div>
      </div>

      {/* Bridge Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Bridge Status
            <Badge variant={data?.connected ? 'default' : 'secondary'}>{data?.bridgeStatus}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Lock className="w-4 h-4 text-primary" />
            <span className="font-medium">Read-Only Mode</span>
            <span className="text-muted-foreground">— Staff Hub cannot write to Daily Ops</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Last checked: {data?.lastChecked ? format(parseISO(data.lastChecked), 'dd MMM yyyy, HH:mm') : '—'}</span>
          </div>
          <div className="bg-muted/50 rounded-md p-3 flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">
              This bridge provides safe, read-only access to Daily Ops operational summaries. 
              No data is written back to Daily Ops from Staff Hub. Staff members see only their own 
              linked items. Managers see team-level summaries.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Missing Data / Notices */}
      {data?.missingData && data.missingData.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Bridge Notices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.missingData.map((msg, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">•</span>
                  {msg}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      {data?.items && data.items.length > 0 ? (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Operational Items</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.items.map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                  <div>
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.type} · {item.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Unplug className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No Daily Ops items available yet</p>
            <p className="text-xs mt-1">Items will appear here once the bridge is fully configured and syncing.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
